"use client";

import Dropdown from "@/components/common/Dropdown";
import ScrollReveal from "@/components/common/ScrollReveal";
import React from "react";

const ISOStandard = () => {
  return (
    <div className="relative flex flex-col gap-8 sm:gap-10 lg:gap-12 px-4 sm:px-6 lg:px-8 pt-2 pb-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center max-w-[1300px] mx-auto w-full">
        <div className="flex flex-col gap-4 sm:gap-5 lg:gap-6 max-w-full lg:max-w-[534px]">
          <ScrollReveal direction="up" delay={0}>
            <p className="font-[var(--font-avenir)] text-text-highlight text-sm sm:text-base leading-relaxed">
              ISO27001 Providing Comprehensive Information Security Management
              Solutions.
            </p>
          </ScrollReveal>

          <ScrollReveal direction="up" delay={100}>
            <p className="text-body-1 text-text-highlight leading-6 sm:leading-relaxed">
              ECQ offer professional consulting services for implementing and
              managing the ISO 27001:2013 Information Security Management System
              (ISMS). Our experienced consultants guide organizations through the
              entire process of establishing an effective ISMS, ensuring
              compliance with international standards, and ultimately helping
              businesses protect their valuable information assets.
            </p>
          </ScrollReveal>

          {/* `font-bold` needs the `!` here and on the two headings below:
              `.body-1-heavy` is declared last inside @layer utilities, so it
              beats a plain font-weight utility and these rendered at 600 —
              Avenir's real bold step is 700, so the intent was being lost. */}
          <ScrollReveal direction="up" delay={200}>
            <h4 className="font-avenir body-1-heavy font-bold!">BENEFITS</h4>
          </ScrollReveal>

          <ScrollReveal direction="up" delay={300}>
            <ul className="pl-3 sm:pl-5 list-disc marker:text-[#EC1938] marker:w-[8px] marker:h-[8px] space-y-2 sm:space-y-3">
              <li className="text-body-1 text-text-highlight leading-6 sm:leading-relaxed">
                Implement the internationally recognized Plan-Do-Check-Act (PDCA)
                methodology for effective information security management.
              </li>
              <li className="text-body-1 text-text-highlight leading-6 sm:leading-relaxed">
                Build trust and credibility with clients and stakeholders.
              </li>
              <li className="text-body-1 text-text-highlight leading-6 sm:leading-relaxed">
                Align with globally accepted standards, applicable to both
                domestic and international contexts.
              </li>
              <li className="text-body-1 text-text-highlight leading-6 sm:leading-relaxed">
                Protect critical information assets within the organization.
              </li>
              <li className="text-body-1 text-text-highlight leading-6 sm:leading-relaxed">
                Apply processes compliant with the Personal Data Protection Act
                (PDPA).
              </li>
              <li className="text-body-1 text-text-highlight leading-6 sm:leading-relaxed">
                Foster a culture of information security awareness and
                participation within the organization.
              </li>
            </ul>
          </ScrollReveal>

          <ScrollReveal direction="up" delay={400}>
            <h4 className="font-avenir body-1-heavy font-bold!">SERVICES</h4>
          </ScrollReveal>

          <ScrollReveal direction="up" delay={500}>
            <h4 className="font-avenir body-1-heavy font-bold!">
              ISO/IEC 27001:2013
            </h4>
          </ScrollReveal>
        </div>
        <ScrollReveal direction="up" delay={600}>
          <div className="iso-illustration relative w-full flex justify-center lg:justify-end">
            <svg
              width="486"
              height="410"
              viewBox="0 0 486 410"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              /* Capped below `lg` — at full content width the 486x410 artwork is
                 as tall as the copy beside it on a phone. */
              className="w-full max-w-[300px] sm:max-w-[380px] md:max-w-[430px] lg:max-w-[486px] h-auto"
            >
              <g clipPath="url(#clip0_673_112915)">
                <path
                  d="M387.153 151.822V336.056C387.153 343.687 380.966 349.883 373.346 349.883H22.1345C14.5144 349.883 8.32812 343.687 8.32812 336.056V96.9961C8.32812 89.3651 14.5144 83.1699 22.1345 83.1699H253.895"
                  fill="#15181D"
                />
                <path
                  d="M387.153 151.822V336.056C387.153 343.687 380.966 349.883 373.346 349.883H22.1345C14.5144 349.883 8.32812 343.687 8.32812 336.056V96.9961C8.32812 89.3651 14.5144 83.1699 22.1345 83.1699H253.895H387.153V151.822Z"
                  stroke="white"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M363.919 148.047V291.521C363.919 296.227 360.123 300.029 355.423 300.029H40.0548C35.3553 300.029 31.5586 296.227 31.5586 291.521V118.188C31.5586 113.482 35.3553 109.68 40.0548 109.68H247.734"
                  fill="#15181D"
                />
                <path
                  d="M363.919 148.047V291.521C363.919 296.227 360.123 300.029 355.423 300.029H40.0548C35.3553 300.029 31.5586 296.227 31.5586 291.521V118.188C31.5586 113.482 35.3553 109.68 40.0548 109.68H247.734H363.919V148.047Z"
                  stroke="white"
                  strokeWidth="4"
                  strokeMiterlimit="10"
                />
            <path
              d="M171.004 161.766H51.2344V188.753H171.004V161.766Z"
              fill="#EC1938"
            />
            <path
              d="M189.723 250.575V227.975H195.192V250.575H189.723Z"
              fill="#EC1938"
            />
            <path
              d="M211.784 233.879C211.307 233.294 210.696 232.815 209.952 232.47C209.209 232.124 208.466 231.938 207.775 231.938C207.404 231.938 207.058 231.965 206.687 232.044C206.315 232.098 205.97 232.231 205.678 232.417C205.386 232.603 205.12 232.842 204.934 233.108C204.722 233.4 204.642 233.773 204.642 234.198C204.642 234.57 204.722 234.889 204.881 235.155C205.041 235.421 205.28 235.634 205.572 235.82C205.89 236.006 206.235 236.192 206.66 236.352C207.085 236.511 207.563 236.671 208.094 236.857C208.864 237.123 209.66 237.389 210.484 237.708C211.307 238.027 212.077 238.426 212.74 238.931C213.404 239.436 213.988 240.074 214.44 240.819C214.891 241.563 215.103 242.52 215.103 243.637C215.103 244.94 214.864 246.057 214.386 247.014C213.909 247.971 213.271 248.742 212.448 249.354C211.652 249.965 210.722 250.417 209.687 250.736C208.652 251.029 207.563 251.188 206.474 251.188C204.855 251.188 203.288 250.896 201.801 250.338C200.288 249.779 199.04 248.982 198.031 247.918L201.589 244.275C202.147 244.967 202.863 245.525 203.766 245.977C204.669 246.429 205.572 246.668 206.448 246.668C206.846 246.668 207.244 246.615 207.616 246.535C207.988 246.456 208.333 246.323 208.625 246.11C208.917 245.924 209.129 245.658 209.315 245.339C209.475 245.02 209.581 244.648 209.581 244.196C209.581 243.744 209.475 243.398 209.262 243.105C209.05 242.813 208.758 242.547 208.359 242.281C207.961 242.042 207.483 241.803 206.899 241.616C206.315 241.404 205.651 241.191 204.908 240.952C204.191 240.712 203.474 240.447 202.784 240.127C202.093 239.808 201.483 239.41 200.925 238.904C200.394 238.399 199.943 237.788 199.624 237.096C199.306 236.405 199.12 235.528 199.12 234.491C199.12 233.241 199.385 232.151 199.89 231.273C200.394 230.369 201.058 229.651 201.908 229.066C202.731 228.482 203.66 228.083 204.722 227.817C205.757 227.551 206.82 227.418 207.882 227.418C209.156 227.418 210.457 227.657 211.784 228.109C213.112 228.588 214.28 229.279 215.263 230.183L211.784 233.852V233.879Z"
              fill="#EC1938"
            />
            <path
              d="M242.211 239.196C242.211 240.978 241.892 242.626 241.282 244.088C240.671 245.577 239.821 246.827 238.706 247.891C237.591 248.954 236.317 249.752 234.803 250.337C233.29 250.922 231.67 251.188 229.891 251.188C228.112 251.188 226.493 250.895 225.006 250.337C223.519 249.752 222.218 248.954 221.13 247.891C220.041 246.827 219.191 245.577 218.554 244.088C217.944 242.599 217.625 240.978 217.625 239.196C217.625 237.415 217.944 235.766 218.554 234.304C219.165 232.841 220.015 231.618 221.13 230.581C222.218 229.571 223.519 228.773 225.006 228.215C226.493 227.657 228.139 227.391 229.891 227.391C231.644 227.391 233.29 227.657 234.803 228.215C236.317 228.773 237.618 229.544 238.706 230.581C239.795 231.592 240.644 232.841 241.282 234.304C241.892 235.766 242.211 237.388 242.211 239.196ZM236.37 239.196C236.37 238.212 236.21 237.308 235.892 236.457C235.573 235.607 235.122 234.889 234.564 234.277C234.007 233.666 233.316 233.187 232.52 232.841C231.723 232.496 230.847 232.31 229.891 232.31C228.936 232.31 228.059 232.496 227.289 232.841C226.493 233.187 225.829 233.666 225.245 234.277C224.661 234.889 224.236 235.607 223.917 236.457C223.599 237.308 223.466 238.212 223.466 239.196C223.466 240.18 223.625 241.164 223.944 242.014C224.263 242.865 224.714 243.61 225.272 244.221C225.829 244.833 226.519 245.311 227.289 245.684C228.086 246.029 228.936 246.216 229.891 246.216C230.847 246.216 231.723 246.029 232.493 245.684C233.29 245.338 233.954 244.86 234.538 244.221C235.122 243.61 235.547 242.865 235.865 242.014C236.184 241.164 236.343 240.206 236.343 239.196H236.37Z"
              fill="#EC1938"
            />
            <path
              d="M253.602 250.577V246.11L262.39 238.16C262.948 237.628 263.399 237.096 263.718 236.538C264.063 235.98 264.222 235.368 264.222 234.703C264.222 233.906 263.957 233.241 263.452 232.736C262.948 232.231 262.258 231.991 261.408 231.991C260.558 231.991 259.788 232.31 259.231 232.922C258.673 233.56 258.328 234.384 258.222 235.395L253.098 234.703C253.23 233.56 253.549 232.523 254.027 231.646C254.531 230.742 255.169 229.997 255.912 229.359C256.682 228.721 257.558 228.242 258.541 227.923C259.523 227.604 260.585 227.418 261.674 227.418C262.709 227.418 263.718 227.551 264.7 227.843C265.683 228.136 266.559 228.535 267.302 229.12C268.072 229.705 268.683 230.423 269.134 231.3C269.586 232.177 269.825 233.188 269.825 234.358C269.825 235.155 269.718 235.9 269.506 236.538C269.294 237.176 269.001 237.788 268.63 238.346C268.258 238.904 267.833 239.41 267.355 239.888C266.877 240.367 266.373 240.845 265.869 241.297L260.638 245.95H269.851V250.603H253.549L253.602 250.577Z"
              fill="#EC1938"
            />
            <path
              d="M281.772 250.575H275.559L284.693 232.574H274.02V227.975H290.401V232.255L281.772 250.575Z"
              fill="#EC1938"
            />
            <path
              d="M311.722 239.222C311.722 240.817 311.562 242.333 311.244 243.795C310.925 245.231 310.421 246.507 309.704 247.597C309.014 248.688 308.084 249.565 306.969 250.203C305.854 250.841 304.5 251.187 302.907 251.187C301.314 251.187 299.96 250.868 298.818 250.203C297.703 249.565 296.774 248.688 296.057 247.597C295.34 246.507 294.836 245.231 294.517 243.795C294.198 242.359 294.039 240.817 294.039 239.222C294.039 237.627 294.198 236.111 294.517 234.675C294.836 233.239 295.34 231.99 296.057 230.9C296.774 229.836 297.677 228.959 298.818 228.32C299.933 227.682 301.314 227.363 302.907 227.363C304.5 227.363 305.854 227.682 306.969 228.32C308.084 228.959 308.987 229.809 309.704 230.9C310.394 231.963 310.925 233.239 311.244 234.675C311.562 236.111 311.722 237.627 311.722 239.222ZM306.252 239.222C306.252 238.531 306.199 237.733 306.12 236.882C306.04 236.031 305.881 235.234 305.642 234.516C305.403 233.798 305.084 233.16 304.633 232.654C304.181 232.149 303.624 231.91 302.934 231.91C302.243 231.91 301.659 232.149 301.208 232.654C300.756 233.16 300.411 233.771 300.172 234.516C299.933 235.26 299.774 236.031 299.668 236.882C299.562 237.733 299.535 238.504 299.535 239.222C299.535 239.94 299.588 240.737 299.668 241.588C299.747 242.439 299.907 243.237 300.172 243.981C300.438 244.726 300.756 245.337 301.208 245.843C301.659 246.348 302.217 246.587 302.934 246.587C303.65 246.587 304.208 246.348 304.633 245.843C305.058 245.337 305.403 244.726 305.642 243.981C305.881 243.237 306.04 242.439 306.12 241.588C306.199 240.737 306.252 239.94 306.252 239.222Z"
              fill="#EC1938"
            />
            <path
              d="M332.3 239.222C332.3 240.817 332.141 242.333 331.822 243.795C331.503 245.231 330.999 246.507 330.282 247.597C329.592 248.688 328.662 249.565 327.547 250.203C326.432 250.841 325.078 251.187 323.485 251.187C321.892 251.187 320.538 250.868 319.396 250.203C318.281 249.565 317.352 248.688 316.635 247.597C315.918 246.507 315.414 245.231 315.095 243.795C314.776 242.359 314.617 240.817 314.617 239.222C314.617 237.627 314.776 236.111 315.095 234.675C315.414 233.239 315.918 231.99 316.635 230.9C317.352 229.836 318.255 228.959 319.396 228.32C320.511 227.682 321.892 227.363 323.485 227.363C325.078 227.363 326.432 227.682 327.547 228.32C328.662 228.959 329.565 229.809 330.282 230.9C330.972 231.963 331.503 233.239 331.822 234.675C332.141 236.111 332.3 237.627 332.3 239.222ZM326.83 239.222C326.83 238.531 326.777 237.733 326.698 236.882C326.618 236.031 326.459 235.234 326.22 234.516C325.981 233.798 325.662 233.16 325.211 232.654C324.76 232.149 324.202 231.91 323.512 231.91C322.821 231.91 322.237 232.149 321.786 232.654C321.334 233.16 320.989 233.771 320.75 234.516C320.511 235.26 320.352 236.031 320.246 236.882C320.14 237.733 320.113 238.504 320.113 239.222C320.113 239.94 320.166 240.737 320.246 241.588C320.326 242.439 320.485 243.237 320.75 243.981C321.016 244.726 321.334 245.337 321.786 245.843C322.237 246.348 322.795 246.587 323.512 246.587C324.229 246.587 324.786 246.348 325.211 245.843C325.636 245.337 325.981 244.726 326.22 243.981C326.459 243.237 326.618 242.439 326.698 241.588C326.777 240.737 326.83 239.94 326.83 239.222Z"
              fill="#EC1938"
            />
            <path
              d="M342.784 250.575V233.984L338.589 237.228L335.828 233.425L343.262 227.975H348.148V250.575H342.837H342.784Z"
              fill="#EC1938"
            />
            <g id="Group_3477">
              <path
                d="M477.901 175.539C477.901 173.784 477.715 172.056 477.396 170.381L484.459 164.638C483.689 162.165 482.627 159.719 481.299 157.379C479.945 155.039 478.352 152.912 476.6 150.998L468.104 154.241C465.502 152.008 462.502 150.253 459.209 149.083L457.776 140.096C455.253 139.538 452.625 139.219 449.917 139.219C447.208 139.219 444.58 139.538 442.031 140.096L440.597 149.083C437.305 150.253 434.278 152.008 431.703 154.241L423.207 150.998C421.454 152.912 419.861 155.039 418.507 157.379C417.153 159.719 416.118 162.165 415.348 164.638L422.41 170.381C422.092 172.056 421.906 173.784 421.906 175.539C421.906 177.294 422.092 179.022 422.41 180.697L415.348 186.441C416.118 188.913 417.18 191.359 418.507 193.699C419.861 196.039 421.454 198.166 423.207 200.081L431.703 196.81C434.305 199.044 437.305 200.799 440.597 201.968L442.031 210.982C444.58 211.54 447.208 211.859 449.917 211.859C452.625 211.859 455.227 211.54 457.776 210.982L459.209 201.968C462.502 200.799 465.528 199.044 468.104 196.81L476.6 200.081C478.352 198.166 479.945 196.039 481.299 193.699C482.654 191.359 483.689 188.913 484.459 186.441L477.396 180.697C477.715 179.022 477.901 177.321 477.901 175.539ZM449.917 190.854C441.473 190.854 434.623 183.994 434.623 175.539C434.623 167.084 441.473 160.224 449.917 160.224C458.36 160.224 465.183 167.084 465.183 175.539C465.183 183.994 458.333 190.854 449.917 190.854Z"
                stroke="#EC1938"
                strokeWidth="4"
                strokeMiterlimit="10"
              />
            </g>
            <path
              d="M171.004 161.766H51.2344V300.028H171.004V161.766Z"
              stroke="white"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M62.3828 234.566H80.9417"
              stroke="white"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M62.3828 268.334H80.9417"
              stroke="white"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M80.9453 246.611H104.682"
              stroke="white"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M114.688 246.611H134.813"
              stroke="white"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M146.656 246.611H156.613"
              stroke="white"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M80.9453 277.613H104.682"
              stroke="white"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M114.688 277.613H134.813"
              stroke="white"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M146.656 277.613H156.613"
              stroke="white"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M80.9453 258.363H120.692"
              stroke="white"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M74.918 288.596H122.364"
              stroke="white"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M69.8703 179.102C71.8352 179.102 73.4281 177.507 73.4281 175.539C73.4281 173.572 71.8352 171.977 69.8703 171.977C67.9054 171.977 66.3125 173.572 66.3125 175.539C66.3125 177.507 67.9054 179.102 69.8703 179.102Z"
              stroke="white"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M90.05 178.649C92.0149 178.649 93.6078 177.054 93.6078 175.086C93.6078 173.119 92.0149 171.523 90.05 171.523C88.0851 171.523 86.4922 173.119 86.4922 175.086C86.4922 177.054 88.0851 178.649 90.05 178.649Z"
              stroke="white"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M97.776 201.676L87.793 211.673L97.9353 221.83"
              stroke="white"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M124.433 222.787L134.416 212.79L124.273 202.633"
              stroke="white"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M117.132 196.598L104.945 226.909"
              stroke="white"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M8.32812 300.027H387.153"
              stroke="white"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M150.055 170.834L158.551 179.342"
              stroke="white"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M158.549 170.781L150 179.343"
              stroke="white"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M240.593 408.671H155.711L160.596 349.883H235.177L240.593 408.671Z"
              stroke="white"
              strokeWidth="4"
              strokeMiterlimit="10"
            />
            <path
              d="M128.789 408.67H265.206"
              stroke="white"
              strokeWidth="4"
              strokeMiterlimit="10"
              strokeLinecap="round"
            />
            <path
              d="M179.844 324.863H215.501"
              stroke="#EC1938"
              strokeWidth="4"
              strokeMiterlimit="10"
              strokeLinecap="round"
            />
            <path
              d="M51.2344 188.727H170.978"
              stroke="white"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <g id="Group_3476">
              <circle cx="324" cy="91" r="39" fill="#15181D" />
              <path
                d="M392.409 91.2523C392.409 86.8918 391.957 82.6376 391.188 78.4897L408.684 64.2913C406.746 58.1492 404.171 52.1136 400.825 46.2906C397.48 40.4677 393.524 35.2297 389.196 30.4968L368.142 38.5533C361.716 33.0228 354.229 28.7154 346.078 25.8172L342.52 3.53575C336.254 2.12655 329.723 1.35547 323.059 1.35547C316.394 1.35547 309.836 2.15313 303.544 3.53575L300.013 25.8172C291.862 28.7154 284.374 33.0494 277.949 38.5533L256.894 30.4968C252.567 35.2297 248.611 40.4677 245.265 46.2906C241.92 52.1136 239.344 58.1492 237.406 64.2913L254.93 78.4897C254.133 82.6376 253.682 86.8918 253.682 91.2523C253.682 95.6129 254.133 99.8937 254.93 104.015L237.406 118.213C239.344 124.382 241.92 130.391 245.265 136.214C248.637 142.01 252.567 147.275 256.894 152.034L277.949 143.951C284.374 149.482 291.835 153.816 300.013 156.688L303.544 178.996C309.836 180.378 316.341 181.176 323.059 181.176C329.776 181.176 336.228 180.378 342.52 178.996L346.078 156.688C354.229 153.816 361.716 149.482 368.142 143.951L389.196 152.034C393.524 147.275 397.48 142.01 400.825 136.214C404.171 130.391 406.746 124.355 408.684 118.213L391.188 104.015C391.957 99.8671 392.409 95.6395 392.409 91.2523ZM323.085 129.195C302.137 129.195 285.197 112.204 285.197 91.2523C285.197 70.3003 302.137 53.3367 323.085 53.3367C344.034 53.3367 360.92 70.3003 360.92 91.2523C360.92 112.204 343.954 129.195 323.085 129.195Z"
                fill="#15181D"
                stroke="#EC1938"
                strokeWidth="5"
                strokeMiterlimit="10"
              />
              <path
                d="M323.061 108.588C332.622 108.588 340.372 100.826 340.372 91.2519C340.372 81.6776 332.622 73.916 323.061 73.916C313.5 73.916 305.75 81.6776 305.75 91.2519C305.75 100.826 313.5 108.588 323.061 108.588Z"
                stroke="#EC1938"
                strokeWidth="5"
                strokeMiterlimit="10"
              />
            </g>
          </g>
          <defs>
            <clipPath id="clip0_673_112915">
              <rect width="486" height="410" fill="white" />
            </clipPath>
          </defs>
            </svg>
          </div>
        </ScrollReveal>
      </div>

      {/* Dropdowns Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 w-full max-w-[1300px] mx-auto py-6 md:py-8 lg:py-10 gap-6 lg:gap-8">
        <Dropdown title="ISMS Consulting Process" delay={0}>
          <p className="pl-8 sm:pl-12 text-body-1 text-text-highlight leading-6 sm:leading-relaxed">
            We provide guidance on developing and implementing information
            security policies, procedures, and practices in accordance with
            ISO/IEC 27001:2013 standards. We also assist with analyzing and
            resolving internal issues to ensure compliance with the ISMS
            framework.
          </p>
        </Dropdown>
        <Dropdown title="Internal Audit IS Consulting Process" delay={100}>
          <p className="pl-8 sm:pl-12 text-body-1 text-text-highlight leading-6 sm:leading-relaxed">
            We provide guidance on conducting internal audits to ensure
            compliance with information security standards within the
            organization.
          </p>
        </Dropdown>
        <Dropdown title="Risk Asset Management Methodology & Risk Treatment Plan Workshop" delay={200}>
          <p className="pl-8 sm:pl-12 text-body-1 text-text-highlight leading-6 sm:leading-relaxed">
            Our consultants help organizations assess and manage information
            security risks in compliance with ISO/IEC 27001:2013 standards.
          </p>
        </Dropdown>
        <Dropdown title="ISMS Management Review" delay={300}>
          <p className="pl-8 sm:pl-12 text-body-1 text-text-highlight leading-6 sm:leading-relaxed">
            Our consultants assist organizations in preparing comprehensive
            management reports, designing report templates, and providing
            ongoing monitoring and verification of reporting compliance with
            ISO/IEC 27001:2013 requirements.
          </p>
        </Dropdown>
        <Dropdown title="Information Security Aspects of Business Continuity Management and Table Top Exercise" delay={400}>
          <p className="pl-8 sm:pl-12 text-body-1 text-text-highlight leading-6 sm:leading-relaxed">
            We offer consultation on integrating information security into
            business continuity management, including conducting simulated
            scenario exercises to test preparedness.
          </p>
        </Dropdown>
        <Dropdown title="Support for Accrediting Standard and External Audit" delay={500}>
          <p className="pl-8 sm:pl-12 text-body-1 text-text-highlight leading-6 sm:leading-relaxed">
            We offer consultation on managing
            external audits, arranging for certified auditors, and providing
            guidance throughout the audit process.
          </p>
        </Dropdown>
      </div>

      {/* PDCA / ISO illustration: continuously spinning gears — faithful port of
          the v1 ".iso-standard__img #Group_3476 / #Group_3477" rule
          (transform-origin at each gear centre, SPINNING 4s infinite linear).
          Each <g> rotates about its own bounding-box centre via transform-box. */}
      <style jsx>{`
        .iso-illustration :global(#Group_3476),
        .iso-illustration :global(#Group_3477) {
          transform-box: fill-box;
          transform-origin: center;
          animation: iso-gear-spin 4s linear infinite;
        }
        @keyframes iso-gear-spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .iso-illustration :global(#Group_3476),
          .iso-illustration :global(#Group_3477) {
            animation: none;
          }
        }
      `}</style>
    </div>
  );
};

export default ISOStandard;
